import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  HostListener,
  Input,
  Output,
  inject,
} from '@angular/core';
import { ModeAffichageGraphique } from '../../Services/installation';

export type PeriodeChangee = { mode: ModeAffichageGraphique; dateFin: Date };

type PickerCellule = {
  label: string;
  dateFin: Date;
  actif: boolean;
  desactive: boolean;
  horsPlage: boolean;
};

const LABELS: Record<ModeAffichageGraphique, string> = {
  jour:  'Jour',
  mois:  'Mois',
  annee: 'Année',
};

const MOIS_COURTS = ['jan', 'fév', 'mar', 'avr', 'mai', 'jun', 'jul', 'aoû', 'sep', 'oct', 'nov', 'déc'];
const JOURS_SEMAINE = ['L', 'M', 'M', 'J', 'V', 'S', 'D'];

@Component({
  selector: 'app-chart-period-selector',
  imports: [],
  templateUrl: './chart-period-selector.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ChartPeriodSelectorComponent {
  @Input() mode: ModeAffichageGraphique = 'jour';
  @Input() dateFin: Date = new Date();
  @Input() modes: ModeAffichageGraphique[] = ['jour', 'mois', 'annee'];
  /** Nombre de jours de déplacement en mode `jour`. */
  @Input() pasJour = 3;
  /** Si actif, le mode `jour` navigue de mois en mois en restant au 1er jour. */
  @Input() navigationJourParMois = false;
  /** Si actif, le mode `mois` affiche uniquement l'année dans le libellé. */
  @Input() afficherAnneeSeuleEnModeMois = false;
  @Output() periodeChange = new EventEmitter<PeriodeChangee>();

  private cdr = inject(ChangeDetectorRef);

  readonly labels = LABELS;
  readonly joursSemaine = JOURS_SEMAINE;

  pickerOuvert = false;
  private dateNavPicker = new Date();

  get labelPeriode(): string {
    const fmt = (d: Date) => d.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit' });
    switch (this.mode) {
      case 'mois':
        if (this.afficherAnneeSeuleEnModeMois) {
          return `${this.dateFin.getFullYear()}`;
        }
        return this.dateFin.toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' });
      case 'annee':
        return `${this.dateFin.getFullYear()}`;
      default:
        if (this.navigationJourParMois) {
          return this.dateFin.toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' });
        }
        return fmt(this.dateFin);
    }
  }

  get peutAvancer(): boolean {
    const auj = new Date();
    switch (this.mode) {
      case 'annee':
        return this.dateFin.getFullYear() < auj.getFullYear();
      case 'mois':
        return this.dateFin.getFullYear() < auj.getFullYear()
          || (this.dateFin.getFullYear() === auj.getFullYear()
              && this.dateFin.getMonth() < auj.getMonth());
      default: {
        if (this.navigationJourParMois) {
          return this.dateFin.getFullYear() < auj.getFullYear()
            || (this.dateFin.getFullYear() === auj.getFullYear()
                && this.dateFin.getMonth() < auj.getMonth());
        }
        const fin = new Date(this.dateFin); fin.setHours(0, 0, 0, 0);
        const today = new Date(auj); today.setHours(0, 0, 0, 0);
        return fin < today;
      }
    }
  }

  changerMode(mode: ModeAffichageGraphique) {
    if (this.mode === mode) return;
    this.pickerOuvert = false;
    const now = new Date();
    const dateFin = mode === 'mois' ? new Date(now.getFullYear(), now.getMonth(), 1)
      : mode === 'annee' ? new Date(now.getFullYear(), 0, 1)
      : this.navigationJourParMois ? new Date(now.getFullYear(), now.getMonth(), 1)
      : now;
    this.periodeChange.emit({ mode, dateFin });
  }

  reculer() {
    this.periodeChange.emit({ mode: this.mode, dateFin: this.deplacer(-1) });
  }

  avancer() {
    if (!this.peutAvancer) return;
    this.periodeChange.emit({ mode: this.mode, dateFin: this.deplacer(+1) });
  }

  private deplacer(sens: -1 | 1): Date {
    const fin = new Date(this.dateFin);
    switch (this.mode) {
      case 'jour':
        if (this.navigationJourParMois) {
          fin.setDate(1);
          fin.setMonth(fin.getMonth() + sens);
        } else {
          fin.setDate(fin.getDate() + sens * this.pasJour);
        }
        break;
      case 'mois':
        fin.setDate(1);
        fin.setMonth(fin.getMonth() + sens);
        break;
      case 'annee':
        fin.setFullYear(fin.getFullYear() + sens);
        break;
    }
    return fin;
  }

  @HostListener('document:click')
  fermerPicker() {
    if (this.pickerOuvert) {
      this.pickerOuvert = false;
      this.cdr.markForCheck();
    }
  }

  ouvrirFermerPicker(event: MouseEvent) {
    event.stopPropagation();
    this.pickerOuvert = !this.pickerOuvert;
    if (this.pickerOuvert) {
      this.dateNavPicker = new Date(this.dateFin);
    }
    this.cdr.markForCheck();
  }

  stopPropagation(event: MouseEvent) {
    event.stopPropagation();
  }

  pickerReculer(event: MouseEvent) {
    event.stopPropagation();
    const d = new Date(this.dateNavPicker);
    switch (this.mode) {
      case 'jour':  d.setMonth(d.getMonth() - 1); break;
      case 'mois':  d.setFullYear(d.getFullYear() - 1); break;
      case 'annee': d.setFullYear(d.getFullYear() - 12); break;
    }
    this.dateNavPicker = d;
    this.cdr.markForCheck();
  }

  pickerAvancer(event: MouseEvent) {
    event.stopPropagation();
    const d = new Date(this.dateNavPicker);
    switch (this.mode) {
      case 'jour':  d.setMonth(d.getMonth() + 1); break;
      case 'mois':  d.setFullYear(d.getFullYear() + 1); break;
      case 'annee': d.setFullYear(d.getFullYear() + 12); break;
    }
    this.dateNavPicker = d;
    this.cdr.markForCheck();
  }

  get peutAvancerPicker(): boolean {
    const auj = new Date();
    switch (this.mode) {
      case 'jour': {
        const moisNav = new Date(this.dateNavPicker.getFullYear(), this.dateNavPicker.getMonth(), 1);
        const moisAuj = new Date(auj.getFullYear(), auj.getMonth(), 1);
        return moisNav < moisAuj;
      }
      case 'mois':
        return this.dateNavPicker.getFullYear() < auj.getFullYear();
      case 'annee': {
        const decadeBase = Math.floor(this.dateNavPicker.getFullYear() / 12) * 12;
        return decadeBase + 11 < auj.getFullYear();
      }
    }
  }

  get titrePicker(): string {
    switch (this.mode) {
      case 'jour':
        return this.dateNavPicker.toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' });
      case 'mois':
        return String(this.dateNavPicker.getFullYear());
      case 'annee': {
        const base = Math.floor(this.dateNavPicker.getFullYear() / 12) * 12;
        return `${base} – ${base + 11}`;
      }
    }
  }

  get lignesPicker(): PickerCellule[][] {
    const cellules = this.genererCellules();
    const cols = this.mode === 'jour' ? 7 : 4;
    const lignes: PickerCellule[][] = [];
    for (let i = 0; i < cellules.length; i += cols) {
      lignes.push(cellules.slice(i, i + cols));
    }
    return lignes;
  }

  selectionnerCellule(event: MouseEvent, cellule: PickerCellule) {
    event.stopPropagation();
    if (cellule.desactive) return;
    this.periodeChange.emit({ mode: this.mode, dateFin: cellule.dateFin });
    this.pickerOuvert = false;
    this.cdr.markForCheck();
  }

  private genererCellules(): PickerCellule[] {
    const auj = new Date(); auj.setHours(0, 0, 0, 0);
    switch (this.mode) {
      case 'jour':  return this.cellulesJour(auj);
      case 'mois':  return this.cellulesMois(auj);
      case 'annee': return this.cellulesAnnee(auj);
    }
  }

  private cellulesJour(auj: Date): PickerCellule[] {
    const annee = this.dateNavPicker.getFullYear();
    const mois  = this.dateNavPicker.getMonth();

    const jourSemainePremier = new Date(annee, mois, 1).getDay();
    const padding = jourSemainePremier === 0 ? 6 : jourSemainePremier - 1;

    const dateFinActive = new Date(this.dateFin); dateFinActive.setHours(0, 0, 0, 0);
    const cellules: PickerCellule[] = [];

    for (let i = padding; i > 0; i--) {
      const d = new Date(annee, mois, 1 - i); d.setHours(0, 0, 0, 0);
      cellules.push({ label: String(d.getDate()), dateFin: new Date(d), actif: false, desactive: d > auj, horsPlage: true });
    }

    const dernierJour = new Date(annee, mois + 1, 0).getDate();
    for (let j = 1; j <= dernierJour; j++) {
      const d = new Date(annee, mois, j); d.setHours(0, 0, 0, 0);
      cellules.push({ label: String(j), dateFin: new Date(d), actif: d.getTime() === dateFinActive.getTime(), desactive: d > auj, horsPlage: false });
    }

    let suiv = 1;
    while (cellules.length < 42) {
      const d = new Date(annee, mois + 1, suiv++); d.setHours(0, 0, 0, 0);
      cellules.push({ label: String(d.getDate()), dateFin: new Date(d), actif: false, desactive: d > auj, horsPlage: true });
    }

    return cellules;
  }

  private cellulesMois(auj: Date): PickerCellule[] {
    const annee      = this.dateNavPicker.getFullYear();
    const anneeActive = this.dateFin.getFullYear();
    const moisActif  = this.dateFin.getMonth();

    return MOIS_COURTS.map((label, i) => {
      const finMois = new Date(annee, i + 1, 0); finMois.setHours(0, 0, 0, 0);
      return {
        label,
        dateFin: new Date(annee, i, 1),
        actif: annee === anneeActive && i === moisActif,
        desactive: finMois > auj,
        horsPlage: false,
      };
    });
  }

  private cellulesAnnee(auj: Date): PickerCellule[] {
    const base       = Math.floor(this.dateNavPicker.getFullYear() / 12) * 12;
    const anneeActive = this.dateFin.getFullYear();
    const cellules: PickerCellule[] = [];

    for (let i = 0; i < 12; i++) {
      const annee = base + i;
      const finAnnee = new Date(annee, 11, 31); finAnnee.setHours(0, 0, 0, 0);
      cellules.push({
        label: String(annee),
        dateFin: new Date(annee, 0, 1),
        actif: annee === anneeActive,
        desactive: finAnnee > auj,
        horsPlage: false,
      });
    }
    return cellules;
  }
}
